import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Alert, Spinner, Modal } from 'react-bootstrap';
import { useTheme } from '../contexts/ThemeContext';

const LogsViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();

  useEffect(() => {
    fetchLogs();
    // Set up auto-refresh every 5 seconds
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/logs');
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setError('');
      } else {
        setError('Failed to fetch logs');
      }
    } catch (err) {
      setError('Error fetching logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getLogLevelBadge = (level) => {
    const levelConfig = {
      'INFO': { variant: 'info', icon: 'info-circle' },
      'WARN': { variant: 'warning', icon: 'exclamation-triangle' },
      'ERROR': { variant: 'danger', icon: 'times-circle' },
      'DEBUG': { variant: 'secondary', icon: 'bug' },
      'ATO_AUTH': { variant: 'primary', icon: 'shield-alt' },
      'ATO_SUBMISSION': { variant: 'success', icon: 'paper-plane' },
      'STP_EVENT': { variant: 'info', icon: 'users' }
    };

    const config = levelConfig[level] || levelConfig['INFO'];
    return (
      <Badge bg={config.variant} className="d-flex align-items-center">
        <i className={`fas fa-${config.icon} me-1`}></i>
        {level}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const filteredLogs = logs.filter(log => {
    const matchesFilter = filter === 'all' || log.level === filter;
    const matchesSearch = !searchTerm || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.level.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.metadata && JSON.stringify(log.metadata).toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const handleLogClick = (log) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  const clearLogs = async () => {
    try {
      const response = await fetch('/api/logs/clear', {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setLogs([]);
        setError('');
      } else {
        setError('Failed to clear logs');
      }
    } catch (err) {
      setError('Error clearing logs: ' + err.message);
    }
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Level', 'Message', 'Metadata'],
      ...filteredLogs.map(log => [
        formatTimestamp(log.timestamp),
        log.level,
        log.message.replace(/,/g, ';'),
        log.metadata ? JSON.stringify(log.metadata).replace(/,/g, ';') : ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-list-alt me-2"></i>
        System Logs Viewer
      </h3>

      {/* Controls */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Log Controls</span>
          <div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={fetchLogs}
              disabled={loading}
              className="me-2"
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <i className="fas fa-sync-alt"></i>
              )}
              Refresh
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              onClick={exportLogs}
              className="me-2"
            >
              <i className="fas fa-download"></i>
              Export
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={clearLogs}
            >
              <i className="fas fa-trash"></i>
              Clear
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Level</Form.Label>
                <Form.Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Levels</option>
                  <option value="INFO">Info</option>
                  <option value="WARN">Warning</option>
                  <option value="ERROR">Error</option>
                  <option value="DEBUG">Debug</option>
                  <option value="ATO_AUTH">ATO Auth</option>
                  <option value="ATO_SUBMISSION">ATO Submission</option>
                  <option value="STP_EVENT">STP Event</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Search Logs</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by message, level, or metadata..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Logs Table */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>System Logs ({filteredLogs.length} entries)</span>
            <small className="text-muted">
              Auto-refreshing every 5 seconds
            </small>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <Table striped hover className="mb-0">
              <thead style={{ position: 'sticky', top: 0, background: colors.surface, zIndex: 1 }}>
                <tr>
                  <th>Timestamp</th>
                  <th>Level</th>
                  <th>Message</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      {loading ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                      ) : (
                        <i className="fas fa-inbox me-2"></i>
                      )}
                      {loading ? 'Loading logs...' : 'No logs found'}
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log, index) => (
                    <tr 
                      key={index}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleLogClick(log)}
                    >
                      <td>
                        <small className="text-muted">
                          {formatTimestamp(log.timestamp)}
                        </small>
                      </td>
                      <td>{getLogLevelBadge(log.level)}</td>
                      <td>
                        <div style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.message}
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLogClick(log);
                          }}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Log Details Modal */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            Log Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Timestamp:</strong>
                  <div className="text-muted">{formatTimestamp(selectedLog.timestamp)}</div>
                </Col>
                <Col md={6}>
                  <strong>Level:</strong>
                  <div>{getLogLevelBadge(selectedLog.level)}</div>
                </Col>
              </Row>
              
              <div className="mb-3">
                <strong>Message:</strong>
                <div 
                  className="p-2 mt-1" 
                  style={{ 
                    background: colors.background, 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {selectedLog.message}
                </div>
              </div>

              {selectedLog.metadata && (
                <div>
                  <strong>Metadata:</strong>
                  <div 
                    className="p-2 mt-1" 
                    style={{ 
                      background: colors.background, 
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}
                  >
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LogsViewer;
